import {
  format,
  addDays,
  addBusinessDays,
  isWeekend,
  isBefore,
  startOfDay,
} from "date-fns";
import type { TicketRequestPayload } from "@/services/vision-service";

/** Business hours for a visit, in 30-minute slots, excluding the 12:00–14:00 lunch break. */
export const AVAILABLE_HOURS = [
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
] as const;

export function formatUTCDate(timestamp: number): string {
  const d = new Date(timestamp);
  return format(
    new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
    "dd/MM/yyyy",
  );
}

// Canonical copy lives with the map layer (InfoBalloon renders it) — this
// was a byte-identical duplicate of proposal/utils' function. Deep path on
// purpose — the map barrel would drag react-map-gl into every module
// importing these utils.
export { formatAddressShort } from "@/features/vision-360/map/utils/address";

/** Street name only, stripping the house number (no neighborhood suffix). */
export function formatStreetOnly(address: string | undefined): string {
  if (!address) return "";
  return address
    .split(" - ")[0]
    .replace(/,\s*\d+.*$/, "")
    .trim();
}

/** Neighborhood parsed from the Brazilian address format, or "" if not present. */
export function getNeighborhood(address: string | undefined): string {
  if (!address) return "";
  const parts = address.split(" - ");
  if (parts.length > 1) {
    return parts[1].split(",")[0].trim();
  }
  return "";
}

/**
 * The absolute photo indices shown as carousel dots. Up to 7 dots; beyond
 * that a sliding window of 7 keeps the active dot centered (clamped at the
 * ends) — the window edges shrink to hint there are more photos.
 */
export function buildDotWindow(total: number, pos: number): number[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  let start: number;
  if (pos < 3) start = 0;
  else if (pos >= total - 3) start = total - 7;
  else start = pos - 3;
  return Array.from({ length: 7 }, (_, i) => start + i);
}

/**
 * Dot diameter by its position within a 7-slot window. Only shrinks the edge
 * that actually hides more photos (`windowStart` = the window's first abs
 * index, i.e. `buildDotWindow(...)[0]`) — the opposite edge, if it's the true
 * start/end of the list, stays full size. The active dot is never shrunk;
 * callers should pass `isActive ? total : windowPos` or override the result.
 */
export function dotSize(
  windowPos: number,
  total: number,
  windowStart: number,
): number {
  if (total <= 7) return 8;
  const leftHidden = windowStart > 0;
  const rightHidden = windowStart + 6 < total - 1;
  if (windowPos === 0) return leftHidden ? 4 : 8;
  if (windowPos === 1) return leftHidden ? 6 : 8;
  if (windowPos === 5) return rightHidden ? 6 : 8;
  if (windowPos === 6) return rightHidden ? 4 : 8;
  return 8;
}

import type { RankedSpace, SpaceAdjustment } from "../ai-mode/types";
import type { SpaceGroup } from "../ai-mode/utils/comboGrouping";

/**
 * Calculates the Haversine distance between two coordinates in kilometers.
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * City name only, stripping a state/country suffix some proposals include
 * ("São Paulo - SP, Brasil" → "São Paulo") — upstream diagnosis data isn't
 * consistent about this, and individual space records never carry it.
 */
export function extractCityName(targetCity: string | undefined): string {
  return targetCity?.split(/ - |,/)[0]?.trim() ?? "";
}

/**
 * Collapses flat records that are pure data duplicates of the same physical
 * room — same space (`partnerId`), identical positions/size/price AND the
 * same room name — into ONE record, unioning their `groupIds` (and
 * `isIndividual`) so the survivor keeps every role either duplicate had
 * (standalone card and/or membership in a combo).
 *
 * NOT duplicates (kept as separate offers):
 * - identical info from DIFFERENT spaces — two genuine offers;
 * - different room names in the same space — operators with per-position
 *   price tables (e.g. VIP Office) legitimately list several distinct rooms
 *   in one building with identical capacity and price;
 * - both records carrying a `searchId` that DIFFERS — the inventory row id
 *   identifies them as distinct rooms, and vetoes the merge even when
 *   everything else matches. A null `searchId` is compatible with anything
 *   (some origins omit it).
 *
 * Must run BEFORE `groupRoomsIntoCombos`: once a combo has already absorbed
 * two different ids for what is really the same room, no amount of
 * post-grouping filtering can undo it — the combo (or the scheduling flow
 * built from `visitSelectedIds`) would still carry both ids. Doing it here,
 * on the flat list every downstream step reads from (spaces grid, visit
 * selection, scheduling, ticket payload), is what makes "choose one room"
 * hold for the whole flow instead of just the listing screen.
 */
export function mergeDuplicateSpaces<
  T extends Pick<
    RankedSpace,
    | "id"
    | "partnerId"
    | "capacity"
    | "sizeM2"
    | "monthlyPrice"
    | "groupIds"
    | "isIndividual"
  > &
    Partial<Pick<RankedSpace, "roomName" | "searchId">>,
>(spaces: T[]): T[] {
  const spaceKey = (s: T) =>
    s.partnerId != null ? `partner:${s.partnerId}` : `id:${s.id}`;
  const contentKey = (s: T) =>
    `${s.capacity}|${s.sizeM2 ?? ""}|${s.monthlyPrice ?? ""}|` +
    (s.roomName ?? "").trim().toLowerCase();

  // A bucket can hold more than one survivor: same space/specs/name but
  // conflicting searchIds means genuinely distinct rooms living under the
  // same key.
  const survivorIndexesByKey = new Map<string, number[]>();
  const survivors: T[] = [];
  for (const space of spaces) {
    const key = `${spaceKey(space)}:${contentKey(space)}`;
    const indexes = survivorIndexesByKey.get(key) ?? [];
    const matchIndex = indexes.find((i) => {
      const survivor = survivors[i];
      return (
        survivor.searchId == null ||
        space.searchId == null ||
        survivor.searchId === space.searchId
      );
    });
    if (matchIndex == null) {
      survivorIndexesByKey.set(key, [...indexes, survivors.length]);
      survivors.push(space);
      continue;
    }
    const survivor = survivors[matchIndex];
    const mergedGroupIds = Array.from(
      new Set([...(survivor.groupIds ?? []), ...(space.groupIds ?? [])]),
    );
    survivors[matchIndex] = {
      ...survivor,
      // Adopt the concrete searchId so a later record with a DIFFERENT one
      // still gets vetoed instead of matching the null.
      searchId: survivor.searchId ?? space.searchId,
      groupIds: mergedGroupIds.length > 0 ? mergedGroupIds : undefined,
      isIndividual: Boolean(survivor.isIndividual || space.isIndividual),
    };
  }
  return survivors;
}

/**
 * Fundamental rule: never render the same offer twice — but don't hide
 * legitimate distinct offers either. `partnerId` identifies the *space*
 * (building), shared by every room in it (see `comboGrouping.ts`), while
 * `id` identifies one specific private room.
 *
 * - Two or more different private rooms from the same space CAN both be
 *   shown (different room, different specs) — that's not a duplicate.
 * - Combos ("conjuntos") built from varied room combinations of the same
 *   space are also fine (sala 1+2, sala 2+3, sala 3+1) — only the exact
 *   same member set repeated, regardless of order (sala 1+2 vs sala 2+1),
 *   is a duplicate. Combo membership is compared by each member's `id`
 *   (never `partnerId` — every member of a combo already shares the same
 *   space, so that alone can't tell combos apart).
 * - A single card is only a true duplicate when it's the same space AND
 *   shows identical info (positions, size, price) — identical info from a
 *   *different* space is fine and both are kept.
 *
 * Because a room only ever becomes selectable by clicking a rendered card,
 * whichever occurrence this function keeps is the one used throughout the
 * rest of the flow (scheduling, ticket) — the dropped duplicate can never
 * be picked from anywhere else.
 */
export function dedupeRenderGroups<
  T extends Pick<
    RankedSpace,
    | "groupIds"
    | "isIndividual"
    | "id"
    | "partnerId"
    | "capacity"
    | "sizeM2"
    | "monthlyPrice"
  >,
>(groups: SpaceGroup<T>[]): SpaceGroup<T>[] {
  const spaceKey = (s: T) =>
    s.partnerId != null ? `partner:${s.partnerId}` : `id:${s.id}`;
  const contentKey = (s: T) =>
    `${s.capacity}|${s.sizeM2 ?? ""}|${s.monthlyPrice ?? ""}`;

  const seen = new Set<string>();
  return groups.filter((group) => {
    const groupKey =
      group.kind === "combo"
        ? `combo:${group.spaces
            .map((s) => s.id)
            .sort()
            .join(",")}`
        : `single:${spaceKey(group.space)}:${contentKey(group.space)}`;
    if (seen.has(groupKey)) return false;
    seen.add(groupKey);
    return true;
  });
}

/**
 * Business-day dates available for scheduling a visit: tomorrow is never
 * bookable (even if it's a weekday), and 2 more full business days must
 * elapse after that before the window opens — e.g. today Thursday means
 * tomorrow is Friday, +2 business days from there lands on Tuesday; today
 * Friday means tomorrow is Saturday, +2 business days from there ALSO lands
 * on Tuesday. Always spans the full 5 business days from that opening day —
 * the proposal's validity doesn't shrink this, a visit must always have 5
 * days to choose from. A day is only included if at least one of
 * `AVAILABLE_HOURS` still meets the 48h minimum lead time on it (see
 * `isSlotBookable`) — in practice always true given how far out the window
 * already starts, kept as a safety net rather than assumed.
 */
export function getAvailableDates(): Date[] {
  const today = startOfDay(new Date());
  const windowStart = addBusinessDays(addDays(today, 1), 2);
  const windowEnd = addBusinessDays(windowStart, 4);

  const dates: Date[] = [];
  let cursor = windowStart;
  while (!isBefore(windowEnd, cursor)) {
    if (
      !isWeekend(cursor) &&
      AVAILABLE_HOURS.some((hour) => isSlotBookable(cursor, hour))
    ) {
      dates.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
  }
  return dates;
}

/**
 * Business-day dates available for a test-drive (1-day office trial): starts
 * exactly one calendar week out (the same weekday, next week — e.g. today
 * Thursday means the earliest option is next Thursday) and always spans the
 * full 10 business days (two full business weeks) from there — the
 * proposal's validity doesn't shrink this, same as `getAvailableDates` — but
 * a later start (the test-drive lead time is intentionally longer than a
 * regular visit) and no per-hour filtering (it's a whole-day booking, no
 * time slot to check).
 */
export function getAvailableTestDriveDates(): Date[] {
  const today = startOfDay(new Date());
  const windowStart = addDays(today, 7);
  const windowEnd = addBusinessDays(windowStart, 9);

  const dates: Date[] = [];
  let cursor = windowStart;
  while (!isBefore(windowEnd, cursor)) {
    if (!isWeekend(cursor)) {
      dates.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
  }
  return dates;
}

/** Combines a calendar day with a "HH:mm" time string into one local Date. */
export function combineDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(":").map(Number);
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);
  return combined;
}

/** A visit slot must be booked at least 48h ahead of the current moment. */
export function isSlotBookable(date: Date, time: string): boolean {
  const slot = combineDateAndTime(date, time);
  return slot.getTime() - Date.now() >= 48 * 60 * 60 * 1000;
}

export function isValidPhone(value: string): boolean {
  return value.replace(/\D/g, "").length >= 10;
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Builds an "Add to Google Calendar" link for a tentative visit slot. */
export function buildGoogleCalendarUrl({
  title,
  description,
  location,
  start,
  durationMinutes = 30,
}: {
  title: string;
  description: string;
  location?: string;
  start: Date;
  durationMinutes?: number;
}): string {
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const toUtcStamp = (d: Date) =>
    d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    details: description,
    dates: `${toUtcStamp(start)}/${toUtcStamp(end)}`,
  });
  if (location) params.set("location", location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function formatRoomTitle(
  capacity: number,
  sizeM2: number | null | undefined,
  positionsLabel: string,
  sqmLabel: (value: number) => string,
): string {
  const positions = `${capacity} ${positionsLabel}`;
  return sizeM2 ? `${positions} · ${sqmLabel(sizeM2)}` : positions;
}

/** Default Woba credit-bonus rule: 5% of the contract's monthly value,
 * converted to credits at R$80/credit. */
export const CREDIT_BONUS_PERCENT = 0.05;
export const CREDIT_VALUE_BRL = 80;

/**
 * Monthly Woba credits a client earns on a contract: a percentage of the
 * monthly value, converted to credits, always rounded UP so the client
 * never sees (or gets less than) a fractional credit.
 *
 * Example: R$ 24.880/month → 5% = R$ 1.244 → ÷ R$80/credit = 15.55 → 16.
 */
export function calculateMonthlyCredits(
  monthlyValue: number,
  bonusPercent: number = CREDIT_BONUS_PERCENT,
  creditValue: number = CREDIT_VALUE_BRL,
): number {
  if (monthlyValue <= 0) return 0;
  return Math.ceil((monthlyValue * bonusPercent) / creditValue);
}

/**
 * Short client-composed blurb for an office (capacity/size/amenity count) —
 * there's no live AI-summarization step in this static frontend, so this
 * reads straight off the room's own fields instead of a generated summary.
 * When the office is part of a combo (`comboTotal` > 1), a sentence is
 * appended explaining that another office was added to reach the client's
 * full requested capacity — so the room's own numbers here aren't mistaken
 * for the client's total ask.
 */
export function buildOfficeSummary(
  capacity: number,
  sizeM2: number | null | undefined,
  neighborhood: string,
  city: string,
  comboTotal: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const base =
    sizeM2 && neighborhood
      ? t("spaces.detail.summary", {
          capacity,
          size: sizeM2,
          neighborhood,
          city,
        })
      : sizeM2
        ? t("spaces.detail.summaryNoNeighborhood", {
            capacity,
            size: sizeM2,
            city,
          })
        : neighborhood
          ? t("spaces.detail.summaryNoSize", { capacity, neighborhood, city })
          : t("spaces.detail.summaryNoSizeNoNeighborhood", { capacity, city });

  if (comboTotal <= 1) return base;
  return base + t("spaces.detail.summaryComboSuffix", { total: comboTotal });
}

/** Same idea as `buildOfficeSummary`, for the shared building/common-area
 * amenities instead of one specific room. */
export function buildSpaceSummary(
  amenitiesCount: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  return amenitiesCount > 0
    ? t("spaces.detail.spaceSummary", { amenities: amenitiesCount })
    : t("amenities.notAvailable");
}

/**
 * Display name for a specific space, built from the SAME formula as the
 * room card's own title (`formatRoomTitle` on the space's own capacity/size)
 * instead of a generic "Escritório Privativo N" ordinal — every place that
 * names an individual office (map distance list, chat context, drawer
 * title) reuses this so a room is always identified the way the card
 * already identifies it, with no per-caller ordinal label to keep in sync.
 * Appends neighborhood/city parsed from the Brazilian address format
 * ("Rua X, 123 - Bairro, Cidade/UF") when available.
 */
export function getSpaceDisplayName(
  space: RankedSpace,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const label = formatRoomTitle(
    space.capacity,
    space.sizeM2,
    t("photos.positions"),
    (value) => t("spaces.card.sqm", { value }),
  );
  if (space.address) {
    const parts = space.address.split(" - ");
    if (parts.length > 1) {
      const neighborhood = parts[1].split(",")[0].trim();
      if (neighborhood) {
        return `${label} - ${neighborhood}, ${space.city}`;
      }
    }
  }
  return `${label} - ${space.city}`;
}

/** "R$ 5.000" — whole-real BRL used across the pricing UI (cards, drawers).
 * Deliberately pt-BR/R$ in every locale: prices are quoted in reais. */
export function formatMoney(value: number): string {
  return `R$ ${Math.round(value).toLocaleString("pt-BR")}`;
}

/** A single space's own (adjustment-aware) monthly price and price per
 * position — the same lookup `SpacesSection`'s cards/drawer already build
 * inline as `priceFor`/`priceForSpace`, pulled out here so a second caller
 * (the "Fazer proposta" flow, which lives outside `SpacesSection`) doesn't
 * have to re-derive it. */
/** Total mensal of a proposal target — a standalone room, or the sum over
 * every office in a combo, each at its adjusted monthly price. */
export function computeProposalTotal(
  spaces: RankedSpace[],
  spaceAdjustments: SpaceAdjustment[],
): number {
  return spaces.reduce(
    (sum, s) => sum + computeSpacePrice(s, spaceAdjustments).monthlyPrice,
    0,
  );
}

/** The one "what value is on the table" rule for the Fazer proposta flow:
 * a null negotiated value means the original stands (never 0 — the input
 * maps a cleared field to null), and negotiating at the same value is not
 * negotiating at all. */
export function computeProposalValues(
  spaces: RankedSpace[],
  spaceAdjustments: SpaceAdjustment[],
  negotiatedValue: number | null,
): { originalValue: number; finalValue: number; isNegotiating: boolean } {
  const originalValue = computeProposalTotal(spaces, spaceAdjustments);
  const finalValue = negotiatedValue ?? originalValue;
  return {
    originalValue,
    finalValue,
    isNegotiating: finalValue !== originalValue,
  };
}

export function computeSpacePrice(
  space: RankedSpace,
  spaceAdjustments: SpaceAdjustment[],
): { monthlyPrice: number; pricePerPosition: number } {
  const adjustment = spaceAdjustments.find((a) => a.spaceId === space.id);
  const monthlyPrice = adjustment?.totalPrice ?? space.monthlyPrice ?? 0;
  const clientPrice = adjustment?.clientPrice ?? space.monthlyPrice ?? 0;
  const pricePerPosition =
    clientPrice > 0 && space.capacity > 0
      ? clientPrice / space.capacity
      : space.pricePerWorkstation;
  return { monthlyPrice, pricePerPosition };
}

/** Ops opens the ticket from the Slack notice — the board filtered to the
 * visit-request reason, since `/tickets/open` does not hand back an id to
 * deep-link with (PED-13885 item 3). */
const HUBSPOT_PORTAL_URL = "https://app.hubspot.com/contacts/21669165";

export const HUBSPOT_VISIT_TICKETS_URL = `${HUBSPOT_PORTAL_URL}/objects/0-5/views/all/board?query=visita`;

/** The deal's own record in HubSpot (PED-13885 item 3) — the proposal knows
 * the deal id, so unlike the ticket this one deep-links. */
export function hubspotDealUrl(dealId: string | number): string {
  return `${HUBSPOT_PORTAL_URL}/record/0-3/${dealId}`;
}

/** The proposal's deal id when it is a real HubSpot one, else null. A
 * proposal created from the home chat before its deal exists carries
 * "chat-home-web-<uuid>" instead, and the Zap fails the ticket on anything
 * but the numeric id (PED-14492). */
export function hubspotDealId(
  dealId: string | number | null | undefined,
): string | null {
  const id = String(dealId ?? "").trim();
  return /^\d+$/.test(id) ? id : null;
}

/**
 * The room's two identifiers side by side (PED-13885 item 4): ops works off
 * the Backoffice one (`search_id`), while the proposal carries Vision's own
 * id. Labeling both is what lets a ticket be traced back without guesswork.
 */
export function formatRoomIdsLine(space: {
  id: string;
  searchId?: number | null;
  partnerId?: number;
}): string {
  const bkoId =
    space.searchId != null && space.searchId !== 0 ? space.searchId : "—";
  return `   ID BKO: ${bkoId} | ID Vision: ${space.id} | Space ID: ${space.partnerId ?? "—"}`;
}

/** Every ticket field the SMB proposal flows never fill — spread first, so
 * each flow's payload only states what it actually sends. */
export const EMPTY_TICKET_FIELDS = {
  deal_name: null,
  company_name: null,
  office_type: null,
  city: "",
  neighborhoods: [],
  size_m2: null,
  positions: null,
  budget: "",
  expected_entry_date: "",
  requires_documentation: false,
  required_documents: [],
  required_documents_other: null,
  observations: "",
  search_more_options: null,
  contract_months: null,
  open_to_nearby_neighborhoods: null,
  private_meeting_rooms: null,
  meeting_room_capacities: [],
  willing_to_adapt_amenities: null,
  specific_partner_or_restrictions: "",
} satisfies Partial<TicketRequestPayload>;
